<template>
  <div>

    <error-dialog :status="loadStatus" />
    <error-dialog :status="claimGiftcardStatus" />
    <error-dialog :status="replyStatus" />
    <error-dialog :status="saveStatus" />

    <!-- I want to have a direct link here for when people click on the button in emails -->
    <add-to-calendar-dialog
      v-if="($route.hash == '#add-to-calendar') && order?.event"
      :event="order.event"
      :value="true"
      @input="$router.replace({ ...$route, hash: undefined })"
    />

    <form-rsvp-recipient-dialog
      v-if="isInvitation && (($route.hash == '#rsvp') || showRSVP)"
      :token="readToken || preview?.read_token"
      :order="order"
      :preview="preview"
      :value="true"
      @input="() => {
        showRSVP = false;
        if (showRSVPAsIntercept)
          close(true);
        else if ($route.hash == '#rsvp')
          $router.replace({ ...$route, hash: undefined });
      }"
    />

    <sign-in-dialog ref="sign-in" v-model="showSignIn" />

    <print-dialog v-if="showPrint" :token="readToken || preview?.read_token" v-model="showPrint" />

    <group-video-playback
      v-if="groupVideoBlob"
      dialog mode="open"
      :project-id="groupVideoProjectId"
      v-model="showGroupVideo"
      @slow-playback="onSlowPlayback"
      @play-video="pauseMusic"
    />

    <dialog-scroll-lock v-if="showReply" v-model="showReply" width="500" @input="close(true)">
      <v-card>
        <v-btn class="float-right ma-3" icon @click="close(true)">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <template v-if="!order.skin.disable_reply">
          <v-card-title>Respond to sender{{isGroupSigned ? 's' : ''}}</v-card-title>
          <v-card-subtitle>Send a message back to the {{isGroupSigned ? 'people' : 'person'}} who made you this card</v-card-subtitle>
          <v-card-text>
            <v-textarea
              auto-grow counter="5000" outlined placeholder="Thanks for the card"
              :rules="[ v => (v || '').length <= 5000 || 'Message is too long' ]"
              :disabled="!!(order.sample || (order.state == 'PENDING') || order.verified_recipient?.reply || replyStatus.pending)"
              :value="(order.sample || (order.state == 'PENDING')) ? 'Response disabled for test orders' : order.verified_recipient.reply || replyMsg"
              @input="v => replyMsg = v"
            />
            <div class="d-flex justify-end">
              <v-btn
                color="primary"
                :disabled="!!(!replyMsg || (replyMsg.length > 5000) || order.verified_recipient?.reply)"
                :loading="replyStatus.pending"
                @click="reply"
              >
                <template v-if="order.verified_recipient?.reply">
                  <v-icon class="mr-2" v-html="mdiSendCheck" />Message sent
                </template>
                <template v-else>
                  <v-icon class="mr-2" v-html="mdiSend" />Send message
                </template>
              </v-btn>
            </div>
          </v-card-text>
          <v-divider />
        </template>

        <v-card-title>Share your card on social media?</v-card-title>
        <v-card-text>
          <social-share-btns
            class="fill-width"
            style="max-width: 320px;"
            title="My card on Ellacard"
            description="Check out this card I got sent on Ellacard"
            :url="`https://ellacard.com/open/${readToken}`"
          />
        </v-card-text>

        <v-card-text class="d-flex justify-end">
          <v-btn @click="close(true)">Close</v-btn>
        </v-card-text>

        <template v-if="!hideReplyDetails">
          <v-divider />
          <div class="grey lighten-4 py-3 px-6 caption">
            Verify your email address or phone number to see all the cards you've ever been sent in <router-link :to="{ name: 'account' }">your Ellacard account</router-link>.
          </div>
        </template>
      </v-card>
    </dialog-scroll-lock>

    <giftcard-recipient-choice-dialog
      v-if="showSelectMerchant"
      :amt="showGiftcardBlob.giftcard.amt"
      :currency="showGiftcardBlob.giftcard.cur"
      :default-country="USER_COUNTRY"
      @input="
        v => {
          claimGiftcardMerchant = v || claimGiftcardMerchant;
          showSelectMerchant = false;
        }
      "
    />

    <dialog-scroll-lock-loader
      v-if="showGiftcardBlob && (mode == 'view')"
      width="400"
      :value="!!showGiftcardBlob"
      :loading="loadGiftcardStatus.pending"
      @input="showGiftcardBlob = null"
    >
      <v-card>
        <v-btn class="float-right mt-3 mr-3" icon @click="showGiftcardBlob = null">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title class="pa-4">Claim gift card</v-card-title>
        <v-divider />
        <div class="pa-4">
          <template v-if="!isRecipientChoice(showGiftcardBlob.giftcard)">
            <v-card class="mx-auto" style="border-radius: 8px; max-width: 268px;">
              <v-img :aspect-ratio="337 / 212.5" :src="showGiftcardBlob.giftcard.product_img" />
            </v-card>
            <div class="mb-4 mt-1 overline short text-center">{{showGiftcardBlob.giftcard.product_name}}</div>
          </template>
          <div class="d-flex align-center justify-center text-h3 primary--text">
            <currency class="grey--text text-body-1" :code="showGiftcardBlob.giftcard.cur" />{{(preview && !isOwner && (showGiftcardBlob.user_id != globalUserId) && order.skin.hide_giftcard_totals) ? 'XX' : showGiftcardBlob.giftcard.amt}}
          </div>
        </div>
        <v-card-text
          v-if="!showGiftcardBlob.giftcard.claim_id && !order.verified_recipient && !isRecipientChoice(showGiftcardBlob.giftcard) && !(showGiftcardBlobProduct?.countries || []).includes(USER_COUNTRY)"
          class="warning--text px-4 pb-4"
        >
          <v-icon class="mr-1" small color="warning" v-html="mdiAlert" /> This gift card cannot be used in your country: {{getCountryName(USER_COUNTRY)}}
        </v-card-text>
        <v-divider />
        <div class="pa-4">
          <v-card-text v-if="showGiftcardBlob.giftcard.claim_id" class="pa-0">
            <v-icon class="mr-1" small v-html="mdiAlert" /> This gift card has already been claimed.
          </v-card-text>
          <v-card-text v-else-if="!order.verified_recipient" class="pa-0">
            <v-icon class="mr-1" small v-html="mdiAlert" /> This gift card can only be claimed using a verified private link.
          </v-card-text>
          <template v-else>
            <div class="mb-4">
              <template v-if="isRecipientChoice(showGiftcardBlob.giftcard)">
                Select a merchant and enter your email address to claim this gift card.
              </template>
              <template v-else-if="!showGiftcardBlobProduct">
                This gift card could not be found (inventory may have changed since it was purchased). Please select an alternate merchant and enter your email address to continue.
              </template>
              <template v-else-if="!(showGiftcardBlobProduct?.countries || []).includes(USER_COUNTRY)">
                Please choose an option and enter your email address to continue.
                <v-radio-group hide-details v-model="merchantOpt">
                  <v-radio
                    :label="`Use this gift card in ${getCountryName(showGiftcardBlob.giftcard.country)}`"
                    :value="0"
                    @click="claimGiftcardMerchant = { ...showGiftcardBlobProduct, country: showGiftcardBlob.giftcard.country }"
                  />
                  <v-radio
                    label="Select alternate country / merchant…"
                    :value="1"
                    @click="
                      () => {
                        showSelectMerchant = true;
                        if (claimGiftcardMerchant && (showGiftcardBlob?.giftcard.src == claimGiftcardMerchant.src) && (showGiftcardBlob?.giftcard.product_id == claimGiftcardMerchant.id))
                          claimGiftcardMerchant = null;
                      }
                    "
                  />
                </v-radio-group>
              </template>
              <template v-else>
                Enter your email address to claim this gift card.
              </template>
            </div>
            <v-form v-model="claimGiftcardValid" @submit.prevent="claimGiftcard">
              <v-text-field
                v-if="!showGiftcardBlobProduct || isRecipientChoice(showGiftcardBlob.giftcard) || (USER_COUNTRY != showGiftcardBlob.giftcard.country)"
                v-bind:class="{
                  'mt-n3 ml-8': showGiftcardBlobProduct && !isRecipientChoice(showGiftcardBlob.giftcard) && !(showGiftcardBlobProduct?.countries || []).includes(USER_COUNTRY)
                }"
                name="merchant" label="Merchant" readonly
                :rules="[ v => !!v ]"
                :value="claimGiftcardMerchant?.name"
                @click="showSelectMerchant = true"
              >
                <template v-slot:append>
                  <v-btn v-if="!claimGiftcardMerchant" icon small color="primary" @click="showSelectMerchant = true">
                    <v-icon v-html="mdiChevronRight" />
                  </v-btn>
                  <country-flag
                    v-else-if="claimGiftcardMerchant && (!(showGiftcardBlobProduct?.countries || []).includes(USER_COUNTRY) || (claimGiftcardMerchant.country != USER_COUNTRY))"
                    class="my-n1"
                    :country="claimGiftcardMerchant.country"
                  />
                </template>
                <template v-slot:append-outer>
                  <v-btn
                    v-if="claimGiftcardMerchant"
                    class="pa-0 gc-border"
                    small
                    @click="showSelectMerchant = true"
                  >
                    <v-img
                      class="gc-border"
                      :aspect-ratio="337 / 212.5"
                      :src="claimGiftcardMerchant.img"
                    />
                  </v-btn>
                </template>
              </v-text-field>
              <v-text-field
                type="email" name="email" label="Email address" hide-details
                :rules="[ v => !!(v && EMAIL_REGEX.test(v)) ]"
                v-model="claimGiftcardEmail"
              />
            </v-form>
            <v-card-text class="mt-6 pa-0 warning--text">
              <v-icon class="mr-1" small color="warning" v-html="mdiAlert" /> Please use a <i>personal</i> email address to ensure the redemption instructions do not get blocked by corporate firewalls
            </v-card-text>
          </template>
        </div>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showGiftcardBlob = null">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!!showGiftcardBlob.giftcard.claim_id || !order.verified_recipient || !claimGiftcardValid"
            :loading="claimGiftcardStatus.pending"
            @click="claimGiftcard"
          >
            Claim
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock-loader>

    <giftcard-add-dialog
      v-if="showGiftcardBlob && (mode == 'edit')"
      :order-id="readToken"
      :blob="showGiftcardBlob"
      :locked="!editable"
      :max-value="maxAddGiftcardValue"
      @input="showGiftcardBlob = null; load(true);"
    />

    <dialog-scroll-lock v-if="showGiftcards" v-model="showGiftcards" width="500">
      <v-card>
        <v-btn class="float-right mt-3 mr-3" icon @click="showGiftcards = false">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title>Gift cards</v-card-title>
        <v-card-subtitle class="pt-4">This order contains {{giftcardBlobs.length == 1 ? 'a gift card' : giftcardBlobs.length + ' gift cards'}}. Click on {{giftcardBlobs.length == 1 ? 'it' : 'a gift card'}} to claim it.</v-card-subtitle>
        <v-divider />
        <v-card-text class="pt-4">
          <v-row>
            <template v-for="blob in giftcardBlobs">
              <v-col cols="6" style="position: relative;">
                <v-btn
                  v-blur
                  style="position: absolute; top: 8px; left: 8px; transition: unset; z-index: 2;"
                  rounded dark
                  :color="blob.giftcard.claim_id ? 'grey lighten-1' : 'green lighten-2'"
                  @click="showGiftcardBlob = blob"
                >
                  <currency :code="blob.giftcard.cur" />{{(preview && !isOwner && (blob.user_id != globalUserId) && order.skin.hide_giftcard_totals) ? '' : blob.giftcard.amt}}
                </v-btn>
                <v-card
                  class="ml-4 mt-4"
                  style="border-radius: 8px; z-index: 1;"
                  hover
                  @click="showGiftcardBlob = blob"
                >
                  <v-img :aspect-ratio="337 / 212.5" :src="blob.giftcard.product_img" />
                </v-card>
              </v-col>
            </template>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showGiftcards = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <!-- Note that we only show this component once we've detected our dimensions in order to avoid CLS -->
    <component
      :is="block ? 'div' : DialogScrollLock"
      v-bind="{ [ block ? 'class' : 'content-class' ]: 'fill-height white relative overflow-hidden' }"
      transition="" fullscreen persistent no-click-animation
      :value="true"
    >

      <div v-if="loadStatus.pending" class="fill-height d-flex justify-center align-center" style="z-index: 1;">
        <v-progress-circular indeterminate />
      </div>

      <div
        v-else-if="order"
        v-el-resize="onResize"
        class="fill-height d-flex flex-column relative overflow-hidden"
        v-bind:style="{
          'background-color': order.skin.page_background_color,
          'cursor': isOpen ? null : 'pointer'
        }"
        v-on="(isOpen || (mode == 'edit')) ? {} : { click: () => isOpen = true }"
      >

        <!-- We make sure to track if the 'onResize' handler ran to avoid CLS issues -->
        <template v-if="didResize">

          <confetti
            v-if="order?.skin?.open_effect"
            :key="order.skin.open_effect"
            class="absolute-fill overflow-hidden"
            style="z-index: 2;"
            :effect="order.skin.open_effect"
            :active="!isOpen && !isConfettiLoading"
          />

          <card-background :src="order.skin.page_background_img" />

          <div
            class="header d-flex align-start pa-1 absolute"
            role="navigation" aria-label="Site navigation"
          >
            <div v-if="order.skin.header_logo" class="flex-grow-0 fill-height relative">
              <img class="absolute" height="100%" aria-hidden="true" :src="order.skin.header_logo" />
            </div>

            <v-spacer />

            <v-btn
              v-if="order.allow_open_edit && !preview"
              class="ma-2"
              aria-label="Sign / edit" fab small
              :color="((mode == 'edit') || !order.updated_at) ? 'primary' : null"
              :loading="toggleModeStatus.pending || saveStatus.pending"
              @click.stop="toggleMode"
            >
              <v-icon v-html="(mode == 'edit') ? mdiCheck : mdiPencil" />
              <span class="btn-tooltip bottom">
                {{(mode == 'edit') ? 'Save changes' : 'Sign / edit…'}}
              </span>
              <div
                v-if="!hideTips && !order.updated_at && !didChangeMode"
                class="btn-tooltip btn-tooltip-prominent bottom elevation-20 persistent pa-0"
                style="width: 200px; text-wrap: wrap; z-index: 100;"
              >
                <div class="pa-3">Click here to personalize this envelope and card</div>
                <v-divider />
                <v-btn class="fill-width" text dark @click.stop="hideTips = true">Hide tips</v-btn>
              </div>
            </v-btn>

            <v-btn
              class="ma-2"
              fab small
              :aria-label="envBtnMsg"
              :loading="!!envLoadingState"
              @click.stop="() => { didClickEnv = true; if (!envLoadingState) isOpen = !isOpen; }"
            >
              <v-icon v-html="isOpen ? mdiEmail : mdiEmailOpen" />
              <span class="btn-tooltip bottom">{{envBtnMsg}}</span>
              <div
                v-if="!hideTips && !order.updated_at && !didClickEnv && didChangeMode"
                class="btn-tooltip btn-tooltip-prominent bottom elevation-20 persistent pa-0"
                style="width: 200px; text-wrap: wrap;"
              >
                <div class="pa-3">Click here to switch between the envelope view and the card view</div>
                <v-divider />
                <v-btn class="fill-width" text dark @click.stop="hideTips = true">Hide tips</v-btn>
              </div>
            </v-btn>

            <v-btn
              v-if="giftcardBlobs.length"
              class="ma-2"
              aria-label="Gift cards" fab small
              @click="showGiftcards = true"
            >
              <v-icon v-html="mdiWalletGiftcard" />
              <span class="btn-tooltip bottom">Gift cards…</span>
            </v-btn>

            <v-btn
              v-if="!isStandaloneGroupVideo"
              class="ma-2"
              aria-label="Print" fab small
              @click="tryPrint"
            >
              <v-icon v-html="mdiPrinter" />
              <span class="btn-tooltip bottom">Print</span>
            </v-btn>

            <v-btn
              class="ma-2"
              height="40" color="primary" elevation="5" rounded
              :disabled="mode == 'edit'"
              @click.stop="close(false)"
            >
              {{doneBtnName || (preview ? 'Exit preview' : 'Done')}}
            </v-btn>
          </div>

          <div class="flex-grow-1 relative">

            <order-stepper-theme
              v-if="editable"
              class="absolute-fill"
              style="min-height: 480px; transition: opacity .2s; z-index: 1; top: 73px;"
              v-bind:style="{ opacity: ((mode == 'edit') && (state == 'closed')) ? 1 : 0 }"
              :order="{ ...order, skin: { ...order.skin, header_logo: null,  } }"
              :org-skin="loadedOrgSkin"
              :no-step-to="true"
              :hide-background-confetti="true"
              :edit-to="true"
              :edit-from="true"
              @update-info="updateToFrom"
              @input="updateSkin"
            />

            <!-- We use this to disable clicks on the back of the envelope since many of those internal components usurp any pointer-events settings -->
            <div v-if="editable && (state == 'open')" class="absolute-fill" style="z-index: 1;" />

            <div
              class="env-container absolute-fill pa-3"
              v-bind:style="{
                top: absHeader ? null : '73px',
                opacity: (mode == 'edit') ? '0 !important' : null,
                'pointer-events': (mode == 'edit') ? 'none' : null
              }"
              :class="state"
            >
              <envelope
                ref="env"
                class="fill-height"
                :offset-controls="isStandaloneGroupVideo ? (isInvitation ? 104 : 0) : 68"
                :skin="order.skin"
                :from="order.info.from"
                :to="order.info.to"
                :aspect-ratio="isStandaloneGroupVideo ? 16 / 9 : 5 / 7"
                :contents-width="isStandaloneGroupVideo ? 800 : null"
              >
                <template v-slot:default="{ w }">
                  <v-card v-if="isStandaloneGroupVideo" color="black">
                    <v-responsive :aspect-ratio="16 / 9" class="d-flex text-center align-center">
                      <v-btn fab x-large color="primary"><v-icon x-large v-html="mdiPlay" /></v-btn>
                    </v-responsive>
                  </v-card>
                  <card-preview
                    v-else
                    class="v-card v-sheet"
                    mode="view"
                    :card="cover"
                    :is-card-owner="isOwner"
                    :is-verified-recipient="isVerifiedRecipient"
                    :width="w"
                  />
                </template>
              </envelope>
            </div>

            <template v-if="order.skin.music && (mode == 'view')">
              <audio ref="music" style="display: none;" :src="order.skin.music" loop @pause="isMusicPlaying = false" />
              <v-fade-transition>
                <div
                  v-if="!didOpen"
                  class="absolute d-flex justify-center pa-3"
                  style="bottom: 0; width: 100%; z-index: 4; pointer-events: none;"
                >
                  <v-btn
                    style="pointer-events: auto;"
                    height="56" elevation="3" color="primary" rounded
                    @click="isOpen = true"
                  >
                    <v-icon class="mr-2" v-html="mdiMusic" /> Click to open
                  </v-btn>
                </div>
              </v-fade-transition>
              <v-fade-transition>
                <v-btn
                  v-if="didOpen"
                  v-blur
                  class="absolute"
                  v-bind:class="{ pulse: isMusicPlaying }"
                  style="bottom: 12px; right: 12px; z-index: 4;"
                  fab height="56" elevation="3"
                  :loading="isMusicLoading"
                  @click.capture.stop.prevent="toggleMusic"
                >
                  <v-icon
                    :color="isMusicPlaying ? 'primary' : 'warning'"
                    v-html="isMusicPlaying ? mdiMusic : mdiMusicOff"
                  />
                </v-btn>
              </v-fade-transition>
            </template>

            <div
              v-if="isStandaloneGroupVideo"
              class="video absolute-fill d-flex flex-column justify-center align-center pa-3" :class="state"
            >
              <group-video-playback
                style="width: 800px; max-width: 100%;"
                value mode="open"
                :project-id="groupVideoProjectId"
                @slow-playback="onSlowPlayback"
                @play-video="pauseMusic"
              />
              <!-- Make sure that the height of this div matches what you set for the offset-controls parameter on the envelope -->
              <div v-if="isInvitation" style="height: 104px;" class="d-flex justify-center align-center">
                <rsvp-btn :pulse="!didClickRSVP" @click="didClickRSVP = true; showRSVP = true;" />
              </div>
            </div>

            <!-- Note that it's important that this component is not visible at the same time as the order-stepper-theme since we allow the latter's envelope to be editable and don't want this component to interfere -->
            <card-editor
              v-else
              ref="card-editor"
              class="card overflow-hidden" :class="[ state, mode ]"
              v-bind:style="{ 'padding-top': absHeader ? 0 : '73px' }"
              :order-id="readToken"
              :mode="mode"
              :card="order"
              :is-owner="isOwner || order.allow_universal_editing"
              :is-verified-recipient="isVerifiedRecipient"
              :grid-padding="73"
              :ctrl-padding-right="order.skin.music && $vuetify.breakpoint.xsOnly && (mode == 'view') ? 68 : 0"
              :allow-replace-design="order.allow_replace_design"
              @click-group-video="showGroupVideo = true"
              @click-giftcard="x => showGiftcardBlob = x"
              @click-rsvp="showRSVP = true"
              @page-group-video="() => { if (!didShowGroupVideo) showGroupVideo = true; }"
              @play-video="pauseMusic"
              @reload="load(true)"
              @input="dirty = true; enqueueSave(3000);"
              @save="save"
            />

          </div>
        </template>
      </div>

      <v-sheet v-else-if="notFound && !isRedirecting" class="fill-height d-flex flex-column justify-center align-center pa-4">
        <div class="title">Card not found</div>
        <v-btn class="mt-8" :to="{ name: 'start' }">Home</v-btn>
        <v-btn class="mt-8" :to="{ name: 'catalog' }">Browse cards</v-btn>
        <div class="mt-8 text-center">
          Think you're seeing this page in error? Please let us know!<br>
          <a href="mailto:support@ellacard.com">support@ellacard.com</a>
        </div>
      </v-sheet>

    </component>
  </div>
</template>


<style scoped>
.env-container.open, .card.closed, .video.closed {
  opacity: 0;
  pointer-events: none;
  /* Without this delay, there would be a slight flicker due to an imperfect cross-fade since the card-editor has its own intrinsic fade-out */
  transition: opacity .4s .2s;
}
.card.closed.edit {
  transition: opacity .4s;
}
.video.closed {
  /* When we're dealing with a standalone video, there is no intrinsic fade, so don't delay */
  transition: opacity .4s;
}
.env-container.closed, .card.open, .video.open {
  opacity: 1;
  transition: opacity .4s;
}

.header {
  height: 73px;
  width: 100%;
  z-index: 2;
  pointer-events: none;
}
.header :not(.spacer) {
  pointer-events: auto;
}
</style>


<style>
input[name="merchant"] {
  font-size: 0.75rem !important;
  font-weight: 500;
  letter-spacing: 0.1666666667em !important;
  text-transform: uppercase;
}

/* Use the following settings to get a splay pages effect that has no buttons with a screen resolution of 1280x720 - note that this is for three pages */
/*
.header.d-flex.align-start.pa-1.absolute,
.card.overflow-hidden .btn-top {
  display: none !important;
}
.page-transition-container:nth-child(1) {
  left: calc(34% - 224.286px) !important;
  transform: scale(.9) rotate(-5deg) !important;
}
.page-transition-container:nth-child(2) {
  left: calc(50% - 224.286px) !important;
  transform: scale(.9) !important;
}
.page-transition-container:nth-child(3) {
  left: calc(66% - 224.286px) !important;
  transform: scale(.9) rotate(5deg) !important;
}
*/

</style>


<script>
import AddToCalendarDialog from '@/components/AddToCalendarDialog.vue';
import CardBackground from '@/components/CardBackground.vue';
import CardEditor from '@/components/CardEditor.vue';
import CardPreview from '@/components/CardPreview.vue';
import ClipWaves from '@/components/ClipWaves.vue';
import Confetti from '@/components/Confetti.vue';
import CountryFlag from '@/components/CountryFlag.vue';
import Currency from '@/components/Currency.vue';
import DialogScrollLock from '@/components/DialogScrollLock.vue';
import Envelope from '@/components/Envelope.vue';
import FormRsvpRecipientDialog from '@/components/FormRsvpRecipientDialog.vue';
import GiftcardAddDialog from '@/components/GiftcardAddDialog.vue';
import GroupVideoPlayback from '@/components/GroupVideoPlayback.vue';
import OrderStepperTheme from '@/components/OrderStepperTheme.vue';
import PrintDialog from '@/components/PrintDialog.vue';
import RsvpBtn from '@/components/RsvpBtn.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import SocialShareBtns from '@/components/SocialShareBtns.vue';
import DialogScrollLockLoader from '@/components/DialogScrollLockLoader.vue';
import VideoPlayer from '@/components/VideoPlayer.vue';
import getCountryName from '@/filters/country-name.js';
import { orgSkin } from '@/mixins/org-skin.js';
import { publicDesign } from '@/mixins/public-design.js';
import { cache, ssr } from '@/mixins/ssr.js';
import getBlobMediaFiles from '@/utils/card-media.js';
import { isBlobVisible } from '@/utils/edit-order.js';
import { EMAIL_REGEX } from '@/utils/email.js';
import { COUNTRY_CODE, CURRENCY_CODES, LOCALE, TZ } from '@/utils/intl.js';
import { findGroupVideoBlob, getOrderType } from '@/utils/group-video.js';
import modernize from '@/utils/order-modernize.js';
import { DEFAULT as DEFAULT_SKIN } from '@/utils/skin.js';
import stripHTML from '@/utils/strip-html.js';
import { mdiEmail, mdiEmailOpen, mdiClose, mdiPrinter, mdiHome, mdiAlert, mdiWalletGiftcard, mdiPlay, mdiSend, mdiSendCheck, mdiMusic, mdiMusicOff, mdiChevronRight, mdiPencil, mdiCheck } from '@mdi/js';


// If this is not defined, then we don't support claiming gift cards in the user's country
const USER_CURRENCY = CURRENCY_CODES[COUNTRY_CODE];


function postMessageOnClose() {
  // Legacy event
  window.top.postMessage(JSON.stringify({ url: location.href }), '*');

  $event.log('embed-msg', { src: 'ellacard-embed', key: 'open:close', data: {} });
  window.top.postMessage(JSON.stringify({ src: 'ellacard-embed', key: 'open:close', data: {} }), '*');
}


export default {
  name: 'card-open',

  routerViewMeta: {
    $hide: [ 'header', 'footer' ]
  },

  components: {
    AddToCalendarDialog,
    CardBackground,
    CardEditor,
    CardPreview,
    ClipWaves,
    Confetti,
    CountryFlag,
    Currency,
    Envelope,
    FormRsvpRecipientDialog,
    GiftcardAddDialog,
    GroupVideoPlayback,
    OrderStepperTheme,
    PrintDialog,
    RsvpBtn,
    SignInDialog,
    SocialShareBtns,
    DialogScrollLockLoader,
    VideoPlayer,
    GiftcardRecipientChoiceDialog: () => import('@/components/GiftcardRecipientChoiceDialog.vue')
  },

  mixins: [ orgSkin, publicDesign, ssr ],

  // Note: 'readToken' is the only URL prop
  props: [ 'readToken', 'preview', 'doneBtnName', 'block', 'back' ],

  data() {
    return {
      order: null,
      dirty: false,
      notFound: false,
      absHeader: false,
      isOpen: false,
      didOpen: false,
      isConfettiLoading: false,
      state: 'closed',
      mode: 'view',
      didChangeMode: false,
      didClickEnv: false,
      hideTips: false,
      showGroupVideo: false,
      didShowGroupVideo: false,
      showRSVP: false,
      showRSVPAsIntercept: false,
      didShowRSVP: false,
      showGiftcards: false,
      merchantOpt: -1,
      showGiftcardBlob: null,
      showGiftcardBlobProduct: null,
      showSelectMerchant: false,
      claimGiftcardEmail: null,
      claimGiftcardMerchant: null,
      claimGiftcardValid: false,
      render: null,
      rsvp: null,
      didResize: false,
      showPrint: false,
      showSignIn: false,
      showReply: false,
      replyMsg: null,
      didClickRSVP: false,
      isMusicLoading: false,
      isMusicPlaying: false,
      envLoadingState: null,
      isRedirecting: false,
      loadedOrgSkin: {},
      loadStatus: this.$track('load'),
      claimGiftcardStatus: this.$track('claimGiftcard'),
      replyStatus: this.$track('reply'),
      loadGiftcardStatus: this.$track('loadGiftcard'),
      saveStatus: this.$track('save'),
      toggleModeStatus: this.$track('toggleMode')
    };
  },

  computed: {
    hydrated() {
      return !this.loadStatus.pending;
    },

    routerViewMeta() {
      const o = this.order;
      if (!o)
        return {};

      const getTxt = (key, prefix) => {
        const value = stripHTML(this.$route.query[key] || o.info[key] || '')
        return value ? (prefix ? ` ${key} ${value}` : value) : '';
      }

      const meta = {};

      if (o.type == 'PERSONAL') {
        meta.title = 'Open card';
        meta.description = `Greeting card${getTxt('from')}${getTxt('to')}`;
      } else if (o.type == 'PERSONAL') {
        meta.title = 'Open card';
        meta.description = `Group greeting card${getTxt('from')}${getTxt('to')}`;
      } else if (o.type == 'INVITATION') {
        meta.title = 'Open invitation and RSVP';
        const host = getTxt('from', false);
        if (o.event.name || host) {
          meta.description = 'Invitation';
          if (o.event.name)
            meta.description += ': ' + o.event.name;
          if (host)
            meta.description += ' hosted by ' + host;
        }
      }

      meta.$canonical = `/open/${this.readToken}`;
      const toFromParams = _.pick(this.$route.query, [ 'from', 'to' ]);
      if (!_.isEmpty(toFromParams))
        meta.$canonical += '?' + new URLSearchParams(toFromParams).toString();

      meta.image = `https://service.ellacard.com/render-envelope/${this.readToken}?${(new URLSearchParams({ bg: 1, ...toFromParams })).toString()}`;
      meta['image:type'] = 'image/png';
      meta['image:width'] = '1280';
      meta['image:height'] = '720';
      return meta;
    },

    loadTrigger() {
      return this.$route.query?.org + this.$route.query?.skin + this.readToken;
    },

    groupVideoBlob() {
      return this.order && findGroupVideoBlob(this.order);
    },

    groupVideoProjectId() {
      return this.groupVideoBlob && this.groupVideoBlob.group_video.project_id;
    },

    orderType() {
      return this.order && getOrderType(this.order);
    },

    isStandaloneGroupVideo() {
      return this.orderType == 'video';
    },

    giftcardBlobs() {
      if (!this.order)
        return [];
      const blobs = [];
      for (const page of this.order.pages)
        for (const blob of page.blobs)
          if ((blob.type == 'GIFTCARD') && (!this.order.skin.private_pages || this.isOwner || (blob.user_id == this.globalUserId) || this.isVerifiedRecipient))
            blobs.push(blob);
      return blobs;
    },

    maxAddGiftcardValue() {
      const o = this.order;
      const profile = this.global.profile;
      let total = 0;
      for (const blob of this.giftcardBlobs)
        total += blob.giftcard.amt;
      if ((!o.org_id && (profile?.user_id == o.user_id)) || ((profile?.org_id == o.org_id) && profile?.org_admin))
        // For org orders, only org admins can add gift cards / value
        // For non-org orders, only the order's owner can add gift cards / value
        return 500000 - total;
      if (o.hide_giftcards)
        return 0;
      if (o.skin.max_giftcard_value == 0)
        return 0;
      return (o.skin.max_giftcard_value || 500000) - total;
    },

    cover() {
      // The cover with static blobs to use as a preview when removing from the envelope
      if (!this.order)
        return {};
      if (this.isStandaloneGroupVideo)
        return { pages: [], background: { img: null, color: '#000' } };
      const pages = this.order.pages.length ? [ _.cloneDeep(this.order.pages[0]) ] : [];
      if (pages.length) {
        for (const blob of pages[0].blobs) {
          if ((blob.type == 'IMG') && blob.img.src_static) {
            blob.img.src = blob.img.src_static;
            blob.img.is_mp4 = false;
            blob.img.try_webp = false;
          }
        }
      }
      return { design_id: this.order.design_id, pages, background: this.order.background, skin: this.order.skin };
    },

    isInvitation() {
      return this.order?.type == 'INVITATION';
    },

    isGroupSigned() {
      let userId;
      for (const page of this.order.pages) {
        for (const blob of page.blobs) {
          if (!blob.user_id)
            continue;
          if (userId && (blob.user_id != userId))
            return true;
          if (!userId)
            userId = blob.user_id;
        }
      }
      return false;
    },

    hideReplyDetails() {
      return this.IS_IFRAME || (this.$route.query.done == 'post-message') || this.back || this.order.skin.done_url || this.order.skin.hide_account;
    },

    isVerifiedRecipient() {
      return !!this.order?.verified_recipient;
    },

    envBtnMsg() {
      if (this.mode == 'edit')
        return this.isOpen ? 'Show envelope' : 'Show card';
      if (this.envLoadingState)
        return this.isOpen ? 'Unfolding…' : 'Folding…';
      return this.isOpen ? 'Close envelope' : 'Open card';
    },

    userId() {
      if (this.global.profile?.org_admin && (this.global.profile.org_id == this.order.org_id))
        return this.order.user_id;
      return this.globalUserId;
    },

    isOwner() {
      return (this.order || this.preview).user_id == this.global.profile?.user_id;
    },

    isOrgAdmin() {
      return this.global.profile?.org_admin && (this.global.profile.org_id == this.order.org_id)
    },

    editable() {
      if (this.preview || !this.order || !this.order.allow_open_edit || (this.order.state != 'COMPLETED'))
        return false;
      if (this.isOwner || this.isOrgAdmin)
        return true;
      if (this.order.transferable && !this.order.lock)
        // Note that we must claim ownership of this order before we try to edit it
        return true;
      if (this.order.allow_universal_editing && !this.order.lock)
        return true;
      return false;
    }
  },

  created() {
    Object.assign(this, { DialogScrollLock, getCountryName, EMAIL_REGEX, USER_COUNTRY: COUNTRY_CODE, USER_CURRENCY, mdiEmail, mdiEmailOpen, mdiClose, mdiPrinter, mdiHome, mdiAlert, mdiWalletGiftcard, mdiPlay, mdiSend, mdiSendCheck, mdiMusic, mdiMusicOff, mdiChevronRight, mdiPencil, mdiCheck });
  },

  mounted() {
    addEventListener('blur', this.pauseMusic);
  },

  beforeDestroy() {
    removeEventListener('blur', this.pauseMusic);
  },

  methods: {
    onResize({ width, height }) {
      this.absHeader = width / height > 1.5;
      this.didResize = true;
    },

    async loadOrder() {
      let order;
      if (this.preview) {
        order = _.cloneDeep(this.preview);
        modernize(order);
      } else {
        try {
          order = await cache(this.$call)('open-order', { read_token: this.readToken, no_update: true });

          if (order.err == 'PENDING') {
            // We're not signed in as the user who owns this order
            const baseMsg = 'To view this test card, you must be signed in as the user who created it (recipients will not see this message)';
            if (this.isSignedIn) {
              const response = await this.$confirm({
                title: 'Switch accounts?',
                msg: baseMsg + '. Do you want to switch accounts?'
              });
              if (response == 'No') {
                await this.$router.push({ name: 'default' });
                return null;
              }
              this.global.jwt = null;
              await this.$nextTick();
            } else {
              const response = await this.$confirm({
                title: 'Sign in?',
                msg: baseMsg + '. Do you want to sign in?',
                buttons: [ 'Cancel', 'Sign in…' ],
                primary: 'Sign in…'
              });
              if (response == 'Cancel') {
                await this.$router.push({ name: 'default' });
                return null;
              }
              await this.$refs['sign-in'].prompt({ title: 'Sign in', subtitle: baseMsg });
              if (!this.isSignedIn) {
                await this.$router.push({ name: 'default' });
                return null;
              }
              order = await this.loadOrder();
              if (!order)
                return null;
            }
          }

          if (!this.IS_IFRAME && order.base_url) {
            this.isRedirecting = true;
            const url = new URL(order.base_url);
            url.searchParams.set('q', `open/${this.readToken}`);
            location.href = url;
            return null;
          }

          modernize(order);

          if (order.skin?.private_pages && order.verified_recipient)
            order.skin.private_pages = false;

          if (order.allow_open_edit && !this.global.profile)
            this.global.jwt = await this.$call('get-jwt', { method: 'ANON' });

        } catch (e) {
          if (e.type == 'NotFound')
            return null;
          else
            throw e;
        }
      }

      const q = this.$route.query;
      if (q.from)
        order.info.from = q.from;
      if (q.to)
        order.info.to = q.to;

      // It's possible to override an order's skin by passing either an org ID (with custom branding) or a serialized
      // skin itself. Note that in the case of 'allow_open_edit', we store the org skin instead of just assigning it to
      // the order since it will restrict our editing capabilities. Otherwise, we don't need to fetch it at all.
      if (q.org) {
        this.loadedOrgSkin = await this.getOrgSkin(q.org);
        Object.assign(order.skin, _.omitBy(this.loadedOrgSkin, _.isNull));
      } else if (q.skin) {
        try {
          this.loadedOrgSkin = JSON.parse(decodeURIComponent(q.skin));
          Object.assign(order.skin, _.omitBy(this.loadedOrgSkin, _.isNull));
        } catch (e) {
          console.warn('Error unpacking skin');
          this.loadedOrgSkin = {};
        }
      } else if (order.allow_open_edit && order.org_id) {
        this.loadedOrgSkin = await this.getOrgSkin(order.org_id);
      } else {
        this.loadedOrgSkin = {};
      }

      // Note that we don't want users to be able to edit the logo here for simplicity, so make sure we force our
      // defaults if the logo is not specified in the loadedOrgSkin already.
      if (order.allow_open_edit)
        this.loadedOrgSkin = Object.assign(
          _.pick(DEFAULT_SKIN, [ 'header_logo', 'email_footer_logo', 'email_footer_url' ]),
          this.loadedOrgSkin
        );

      order.pages = order.pages.filter(x => x.blobs.some(x => isBlobVisible(x, 'view', order.design_id)));
      return order;
    },

    async load(reload=false) {
      if (!reload) {
        this.isOpen = false;
        this.notFound = false;
        this.showGroupVideo = false;
        this.didShowGroupVideo = false;
      }

      const order = await this.loadOrder();
      if (order) {
        const prevOpenEffect = this.order?.skin?.open_effect;

        this.order = order;

        const curOpenEffect = this.order?.skin?.open_effect;
        if (curOpenEffect && (curOpenEffect != prevOpenEffect)) {
          // It's pretty jarring to see the confetti right away (and it glitches), so give it a delay before starting
          this.isConfettiLoading = true;
          setTimeout(() => this.isConfettiLoading = false, 500); // 1000
        }

        if (!order.skin.music && !(this.editable && !order.updated_at)) {
          let delay = 2500;
          if (order.info.from)
            delay += 750;
          if (order.info.to)
            delay += 750;
          if (curOpenEffect)
            delay += 3500;
          setTimeout(() => { if (this.mode == 'view') this.isOpen = true; }, delay);
        }
      } else {
        this.notFound = true;
      }
    },

    async reply() {
      const response = await this.$call('reply-to-delivery', { token: this.readToken, msg: this.replyMsg });
      this.$set(this.order.verified_recipient, 'reply', response.reply.msg);
      if (response.err == 'ALREADY_SENT')
        throw new VisibleError('A response has already been sent');
      await this.$confirm({ title: 'Response sent', buttons: [ 'Ok' ], primary: 'Ok' });
      this.close(true);
    },

    async close(skipReply) {
      if (this.preview) {
        this.$emit('close');
        return;
      }

      if (this.isInvitation && !this.didShowRSVP) {
        this.showRSVP = true;
        this.showRSVPAsIntercept = true;
        return;
      }

      if (!this.isInvitation && !skipReply && !(this.order.skin.disable_reply && this.hideReplyDetails) && !this.rsvp && ((this.order.state == 'PENDING') || (this.order.verified_recipient && !this.order.verified_recipient.link))) {
        this.showReply = true;
        return;
      }

      if (this.$route.query.done == 'post-message') {
        postMessageOnClose();
        return;
      }

      if (this.back) {
        this.$router.back();
        return;
      }

      if (this.order.skin.done_url) {
        const params = {
          user_id: this.globalUserId || '',
          order_id: this.order.id,
          delivery_id: this.readToken,
          design_id: this.order.design_id,
          view: 'open'
        };
        let url = this.order.skin.done_url;
        for (const [ key, value ] of Object.entries(params))
          url = url.replace(`{${key}}`, value);
        window.open(url, '_top')
        return;
      }

      // No harm in posting a message here, and it protects the case where an embedded page does not have a done_url
      // set up
      postMessageOnClose();
      this.$router.push({ name: 'default' });
    },

    tryPrint() {
      if (this.order?.sample)
        this.$confirm({ msg: 'Sample orders cannot be printed', buttons: [ 'Ok' ], primary: 'Ok' });
      else if (this.order?.state == 'PENDING')
        this.$confirm({ msg: 'Printing is disabled until after checkout', buttons: [ 'Ok' ], primary: 'Ok' });
      else if (this.preview)
        this.$confirm({ msg: 'Printing is disabled in preview mode', buttons: [ 'Ok' ], primary: 'Ok' });
      else
        this.showPrint = true;
    },

    async claimGiftcard() {
      const warnings = [];
      const countries = this.claimGiftcardMerchant?.countries || this.showGiftcardBlobProduct?.countries || [];
      if (!countries.includes(COUNTRY_CODE))
        warnings.push(`This gift card can only be used in the following countries: ${countries.map(getCountryName).join(', ')}`);
      if (this.claimGiftcardMerchant && (this.claimGiftcardMerchant.cur != this.showGiftcardBlob.giftcard.cur))
        warnings.push(`Currency exchange rates will apply: ${this.showGiftcardBlob.giftcard.cur} &rarr; ${this.claimGiftcardMerchant.cur}`);
      if (warnings.length && ('No' == await this.$confirm({ title: 'Are you sure?', msg: warnings.join('<br><br>') + '<br><br>Are you sure you want to continue?' })))
        return;

      const { src, id } = this.claimGiftcardMerchant || {};
      if ((id || '').startsWith('VISA') || (id == 'Q24BD9EZ332JT')) {
        const r = await this.$confirm({
          title: '<span class="warning--text">Expiration notice</span>',
          msg: `This virtual VISA card <span class="font-weight-bold warning--text">expires in six months</span> – any remaining value after that time will be forfeit and retained by VISA.<br><br>Please make sure you're ready to use the funds on this card before you claim it.`,
          buttons: [ 'Cancel', 'Ok' ],
          primary: 'Cancel',
          loading: 5000
        });
        if (r == 'Cancel')
          return;
      }

      try {
        const params = {
          email: this.claimGiftcardEmail,
          read_token: this.readToken,
          blob_id: this.showGiftcardBlob.id,
          merchant: this.claimGiftcardMerchant ? { src, id } : undefined,
          locale: LOCALE,
          tz: TZ
        };
        $event.log('claim-giftcard-attempt', params, true);
        const claimId = await this.$call('claim-giftcard', params);
        await this.$confirm({
          title: 'Email sent',
          msg: `You'll receive an email within a few minutes containing more details on how to redeem the gift card.`,
          buttons: [ 'Ok' ],
          primary: 'Ok',
          persistent: false
        });
        this.$set(this.showGiftcardBlob.giftcard, 'claim_id', claimId);
        this.showGiftcardBlob = null;
      } catch (e) {
        if (e.type == 'UnverifiedRecipient' || e.type == 'AntiFraudEmail')
          throw new VisibleError('Only verified recipients can claim gift cards in this order');
        if (e.type == 'AlreadyClaimed')
          throw new VisibleError('This gift card has already been claimed');
        if (e.type == 'AntiFraudHold')
          throw new VisibleError('A hold has been placed on this gift card. Please contact us: <a href="mailto:support@ellacard.com">support@ellacard.com</a>');
        if (e.type == 'AntiFraudDelayHours') {
          const h = parseInt(e.message);
          throw new VisibleError(`This gift card cannot be claimed yet. Please try again in ${h} hour${h == 1 ? '' : 's'}.<br><br>See <a href="/faq#can-i-claim-a-gift-card-right-away">our FAQ</a> for more details.`);
        }
        throw e;
      }
    },

    async openEnv() {
      this.envLoadingState = 'open';
      await this.$refs.env.open(this.mode == 'edit' ? 0 : 3000);
      if (this.envLoadingState != 'open')
        return;
      this.state = 'open';
      if (this.mode != 'edit')
        await new Promise(r => setTimeout(r, 200));
      if (this.envLoadingState == 'open')
        this.envLoadingState = null;
    },

    async closeEnv() {
      this.envLoadingState = 'closed';
      this.state = 'closed';
      if (this.mode != 'edit')
        await new Promise(r => setTimeout(r, 400));
      await this.$refs.env.close(this.mode == 'edit' ? 0 : 3000);
      if (this.envLoadingState == 'closed')
        this.envLoadingState = null;
    },

    onSlowPlayback() {
      if (this.didShowSlowPlayback)
        return;
      this.didShowSlowPlayback = true;
      this.$confirm({ title: 'Slow playback detected', msg: `Don't worry, this is only a <b>preview</b> of your video. We're currently working on building a smooth, high-quality version. Click the download button, and we'll automatically send you an email when it's ready.`, buttons: [ 'Ok' ], primary: 'Ok' });
    },

    playMusic() {
      this.isMusicLoading = true;
      this.$nextTick(() => this.$refs.music.play().then(() => this.isMusicPlaying = this.isMusicLoading).finally(() => this.isMusicLoading = false));
    },

    pauseMusic() {
      this.$refs.music?.pause();
      this.isMusicLoading = false;
      this.isMusicPlaying = false;
    },

    toggleMusic() {
      if (this.isMusicLoading || this.isMusicPlaying)
        this.pauseMusic();
      else
        this.playMusic();
    },

    isRecipientChoice(giftcard) {
      return (giftcard.src == 'TR') && giftcard.product_id.startsWith('ANY-');
    },

    async loadGiftcard({ src, product_id, value }) {
      return await this.$call('get-giftcard', { provider: src, id: product_id, check_availability: value });
    },

    async toggleMode() {
      if (this.mode == 'edit') {
        this.enqueueSave(0);
        setTimeout(() => this.saveStatus.promise.then(() => this.mode = 'view'));
      } else {
        // By nature of being in here, we already know that this order has allow_open_edit set to true
        // if (!this.isSignedIn && this.order.transferable && !this.order.allow_universal_editing) {
        if (!this.editable) {
          // For transferable orders without universal editing, we have to claim ownership before we can sign it
          await this.$refs['sign-in'].prompt({
            title: 'Verify your email address to sign / edit this card',
            subtitle: 'This card will be saved to your Ellacard account so you can make changes later'
          });
          if (!this.editable)
            return;
        }
        if (this.order.transferable && !this.isOwner && !this.isOrgAdmin && !this.global.profile?.anon)
          this.order = await this.$call('claim-order-ownership', { order_id: this.readToken });
        this.mode = 'edit';
      }
    },

    updateToFrom(key, value) {
      if ((this.order.info[key] == value) || (!this.order.info[key] && !value))
        // This can be overzealously triggered by immediate updates
        return;
      this.dirty = true;
      this.$set(this.order.info, key, value);
      this.enqueueSave(3000);
    },

    updateSkin({ skin }) {
      this.dirty = true;
      this.order.skin = skin;
      this.enqueueSave(3000);
    },

    enqueueSave(delay) {
      if (!this.editable)
        return;
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(
        async () => {
          await this.saveStatus.promise;
          this.save();
        },
        delay
      );
    },

    async save() {
      // Do not call this directly, instead call 'enqueueSave' with a delay of 0. Because of that structure, we are
      // effectively guaranteed that we have a lock over this function.
      if (!this.dirty || _.isEqual(this.saving, this.order))
        return;

      this.saving = _.cloneDeep(this.order);
      clearTimeout(this.saveTimeout);

      for (const key of [ 'from', 'to' ])
        if (!this.order.info[key])
          this.order.info[key] = '';
      const files = await getBlobMediaFiles(this.order);
      const params = { order_id: this.readToken, order: this.order, files };
      const { err, order } = await this.$call('update-open-order', params);

      if (err) {
        this.order = order;
        if (err == 'VERSION')
          // The order would have been auto-merged on the backend if possible
          throw new VisibleError('Your version of this card is out of date – your changes have been replaced with the latest version');
        else
          throw new VisibleError('This card cannot be edited');
      }

      const selected = this.$refs['card-editor']?.selected;
      if (selected) {
        for (const page of order.pages) {
          for (let i = 0; i < page.blobs.length; i++) {
            if (page.blobs[i].id == selected.id) {
              page.blobs[i] = selected;
              break;
            }
          }
        }
      }
      this.order = order;

      await this.$nextTick();
      this.dirty = false;
    },

    reportOpened() {
      if (this.preview || (this.state != 'open') || this.didReportOpened)
        return;
      this.didReportOpened = true;
      this.$call('update-card-opened', { token: this.readToken });
    }
  },

  watch: {
    '$route.hash': {
      immediate: true,
      handler(v) {
        if (v == '#rsvp')
          this.didShowRSVP = true;
      }
    },

    readToken: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    isOpen(v) {
      if (v) {
        this.openEnv();
        this.didOpen = true;
      } else {
        this.closeEnv();
      }

      if (this.$refs['card-editor']?.selected)
        this.$refs['card-editor'].selected = null;
    },

    didOpen() {
      if (this.$refs.music)
        this.playMusic();
    },

    showGroupVideo(v) {
      if (v)
        this.didShowGroupVideo = true;
    },

    showRSVP(v) {
      if (v)
        this.didShowRSVP = true;
    },

    async showGiftcardBlob(v) {
      this.merchantOpt = -1;
      this.claimGiftcardMerchant = null;
      if (v)
        this.showGiftcardBlobProduct = await this.loadGiftcard(v.giftcard);
      else
        this.showGiftcardBlobProduct = null;
    },

    claimGiftcardMerchant(v) {
      const { src, product_id } = this.showGiftcardBlob?.giftcard || {};
      if (v && (src == v.src) && (product_id == v.id))
        this.merchantOpt = 0;
      else if (v)
        this.merchantOpt = 1;
    },

    'order.verified_recipient.email'(v) {
      if (v)
        this.claimGiftcardEmail = v;
    },

    preview: {
      handler(v) {
        if (v)
          Object.assign(this.order.skin, v.skin);
      }
    },

    async mode(v) {
      // Any time we change the mode, lock the envelope to whatever state it's in
      this.didChangeMode = true;
      if (this.envLoadingState) {
        this.$refs.env.finishAnimation();
        this.state = this.envLoadingState;
        this.envLoadingState = null;
      }
    },

    state() {
      this.reportOpened();
    }
  }
}
</script>
